import { INDIA_TIME_ZONE } from "@medicorepay/shared";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, CreditCard, Banknote, Receipt } from "lucide-react";
import {
  billingSessionService,
  BillingSession,
  SessionSummary,
} from "../../services/billing-session.service";
import { showToast } from "../../lib/toast";
import { formatDateTime } from "../../lib/utils";

const STATUS_COLORS: Record<string, string> = {
  OPEN: "bg-green-100 text-green-800",
  CLOSED: "bg-gray-100 text-gray-800",
  RECONCILED: "bg-blue-100 text-blue-800",
};

export function ViewBillingSessionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<BillingSession | null>(null);
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadSession();
    }
  }, [id]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const [sessionData, summaryData] = await Promise.all([
        billingSessionService.getById(id!),
        billingSessionService.getSessionSummary(id!).catch(() => null),
      ]);
      setSession(sessionData);
      setSummary(summaryData);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load billing session",
      );
      navigate("/billing-sessions");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-gray-500">Session not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/billing-sessions")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Billing Sessions
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Session: {session.sessionNumber}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {new Date(session.sessionDate).toLocaleDateString("en-IN", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                timeZone: INDIA_TIME_ZONE,
              })}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {session.store?.name ? `Store: ${session.store.name}` : ""}
              {session.counter?.name
                ? ` • Counter: ${session.counter.name}`
                : ""}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 text-sm font-medium rounded-full ${
                STATUS_COLORS[session.status] || "bg-gray-100"
              }`}
            >
              {session.status}
            </span>
            {session.status === "OPEN" && (
              <button
                onClick={() =>
                  navigate(`/billing-sessions/${session.id}/close`)
                }
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Close Session
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Session Details Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Session Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Shift</p>
              <p className="text-sm font-medium text-gray-900">
                {session.shift}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Start Time</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDateTime(session.startTime)}
              </p>
            </div>
          </div>
          {session.endTime && (
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">End Time</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatDateTime(session.endTime)}
                </p>
              </div>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500">Store</p>
            <p className="text-sm font-medium text-gray-900">
              {session.store?.name || "-"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Opened By</p>
            <p className="text-sm font-medium text-gray-900">
              {session.openedByName || session.openedBy}
            </p>
          </div>
          {session.closedBy && (
            <div>
              <p className="text-sm text-gray-500">Closed By</p>
              <p className="text-sm font-medium text-gray-900">
                {session.closedByName || session.closedBy}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Sales Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Banknote className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Sales</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(session.totalSales)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Banknote className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Returns</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(session.totalReturns)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Receipt className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Invoice Count</p>
              <p className="text-xl font-bold text-gray-900">
                {session.invoiceCount}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Banknote className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Opening Cash</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(session.openingCash)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Breakdown */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Payment Breakdown
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="h-4 w-4 text-green-600" />
              <span className="text-sm text-gray-500">Cash</span>
            </div>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(session.totalCashIn - session.totalCashOut)}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-gray-500">Card</span>
            </div>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(session.totalCard)}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="h-4 w-4 text-purple-600" />
              <span className="text-sm text-gray-500">UPI</span>
            </div>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(session.totalUPI)}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="h-4 w-4 text-orange-600" />
              <span className="text-sm text-gray-500">Credit</span>
            </div>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(session.totalCredit)}
            </p>
          </div>
        </div>
      </div>

      {/* Cash Reconciliation (for closed sessions) */}
      {session.status !== "OPEN" && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Cash Reconciliation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500">Expected Cash</p>
              <p className="text-lg font-medium text-gray-900">
                {formatCurrency(session.expectedCash || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Actual Cash</p>
              <p className="text-lg font-medium text-gray-900">
                {formatCurrency(session.actualCash || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Variance</p>
              <p
                className={`text-lg font-medium ${
                  (session.variance || 0) < 0
                    ? "text-red-600"
                    : (session.variance || 0) > 0
                      ? "text-green-600"
                      : "text-gray-900"
                }`}
              >
                {formatCurrency(session.variance || 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Breakdown from Summary */}
      {summary?.paymentBreakdown && summary.paymentBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Payment Method Details
          </h2>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Payment Method
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Count
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {summary.paymentBreakdown.map((item, index) => (
                <tr key={index}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {item.paymentMethod}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                    {item._count}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">
                    {formatCurrency(item._sum.totalAmount || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      {session.notes && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {session.notes}
          </p>
        </div>
      )}
    </div>
  );
}

export default ViewBillingSessionPage;
